import { Button, Drawer, Space, Tabs, Typography } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { userManager } from '@/Core/Managers'

import {
    getBaseColumns,
    getBaseFilterFields,
    getDefaultFilterFieldsOptionKey,
    getDefaultTableColumnsOptionKey,
    getFilterFieldsOptionKey,
    getTableColumnsOptionKey,
    resolveColumnSettings,
    resolveFilterFieldSettings,
    serializeColumnSettings,
    serializeFilterFieldSettings,
    type IResolvedColumn,
    type IResolvedFilterField,
} from '../../Helpers/tableSettings'

import { ColumnsTab } from './ColumnsTab'
import { FilterFieldsTab } from './FilterFieldsTab'

interface TableSettingsDrawerProps {
    crudContext: any
    open: boolean
    onClose: () => void
}

/**
 * Ролі, яким доступне керування глобальними дефолтами ресурсу.
 * ROLE_SUPER_ADMIN додано поряд із ROLE_ADMIN, бо бекенд не завжди розгортає
 * ієрархію ролей у /me (той самий патерн, що в CrudResourceConfig.accessFn).
 */
const DEFAULTS_ADMIN_ROLES = ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']

/**
 * Drawer налаштувань списку з двома вкладками («Колонки» / «Фільтр»).
 *
 * UX: тримаємо локальний draft-стейт; зміни застосовуються і персистяться лише
 * по кнопці «Зберегти» (setUserOption → сервер), причому зберігаються ЛИШЕ
 * реально змінені вкладки — незаймана вкладка не отримує персональної копії
 * і продовжує успадковувати глобальний дефолт ресурсу. «Скинути» видаляє
 * персональні опції ресурсу та ре-резолвить з сервера — користувач бачить
 * глобальний дефолт ресурсу (якщо адмін його задав) або базовий набір.
 *
 * Адмін (ROLE_ADMIN/ROLE_SUPER_ADMIN) додатково може зберегти поточний draft як
 * дефолт для всіх користувачів або скинути цей дефолт (setGlobalOption /
 * deleteGlobalOption, коди з префіксом default_). Після дії кеш ре-резолвиться
 * примусово, щоб зміни підхопилися без перезавантаження сторінки.
 */
export const TableSettingsDrawer = ({ crudContext, open, onClose }: TableSettingsDrawerProps) => {
    const { t } = useTranslation()
    const resourceKey = crudContext?.resource?.key
    const isDefaultsAdmin = userManager.hasAnyRole(DEFAULTS_ADMIN_ROLES)

    const [draftColumns, setDraftColumns] = useState<IResolvedColumn[]>([])
    const [draftFields, setDraftFields] = useState<IResolvedFilterField[]>([])
    const [isDefaultsBusy, setIsDefaultsBusy] = useState(false)

    // Знімки draft на момент відкриття — щоб «Зберегти» писав лише змінені вкладки
    const baselineColumnsRef = useRef('')
    const baselineFieldsRef = useRef('')

    // Ініціалізація draft з актуального набору ресурсу, злитого зі збереженими опціями
    const initDraft = useCallback(() => {
        const savedColumns = resourceKey ? userManager.getUserOption(getTableColumnsOptionKey(resourceKey)) : undefined
        const columns = resolveColumnSettings(getBaseColumns(crudContext), savedColumns)
        setDraftColumns(columns)
        baselineColumnsRef.current = JSON.stringify(serializeColumnSettings(columns))

        const savedFields = resourceKey ? userManager.getUserOption(getFilterFieldsOptionKey(resourceKey)) : undefined
        const fields = resolveFilterFieldSettings(getBaseFilterFields(crudContext?.filterFormContext), savedFields)
        setDraftFields(fields)
        baselineFieldsRef.current = JSON.stringify(serializeFilterFieldSettings(fields))
    }, [crudContext, resourceKey])

    useEffect(() => {
        if (open) initDraft()
    }, [open, initDraft])

    /**
     * Зберігає ЛИШЕ реально змінені вкладки (порівняння зі знімком на відкритті).
     * Персональна копія незайманої вкладки назавжди перекрила б їй глобальний
     * дефолт ресурсу — персональна опція найпріоритетніша в ланцюзі резолву.
     */
    const handleSave = () => {
        if (!resourceKey) return
        const nextColumns = serializeColumnSettings(draftColumns)
        if (JSON.stringify(nextColumns) !== baselineColumnsRef.current) {
            userManager.setUserOption(getTableColumnsOptionKey(resourceKey), nextColumns)
        }
        const nextFields = serializeFilterFieldSettings(draftFields)
        if (JSON.stringify(nextFields) !== baselineFieldsRef.current) {
            userManager.setUserOption(getFilterFieldsOptionKey(resourceKey), nextFields)
        }
        onClose()
    }

    /**
     * Скидання персональних налаштувань. Чекаємо серверне видалення і лише потім
     * ре-резолвимо: інакше resolved повернув би ще не видалену персональну опцію.
     * Після резолву користувач бачить дефолт ресурсу або базовий набір.
     */
    const handleReset = async () => {
        if (!resourceKey) return
        const columnsKey = getTableColumnsOptionKey(resourceKey)
        const filterKey = getFilterFieldsOptionKey(resourceKey)

        await Promise.all([userManager.deleteUserOption(columnsKey), userManager.deleteUserOption(filterKey)])
        await userManager.resolveUserOptions([columnsKey, filterKey], { force: true })
        initDraft()
    }

    /** Адмін: зберегти поточний draft як дефолт ресурсу для всіх користувачів */
    const handleSaveDefault = async () => {
        if (!resourceKey) return
        setIsDefaultsBusy(true)
        try {
            await Promise.all([
                userManager.setGlobalOption(
                    getDefaultTableColumnsOptionKey(resourceKey),
                    serializeColumnSettings(draftColumns)
                ),
                userManager.setGlobalOption(
                    getDefaultFilterFieldsOptionKey(resourceKey),
                    serializeFilterFieldSettings(draftFields)
                ),
            ])
            await userManager.resolveUserOptions(
                [getTableColumnsOptionKey(resourceKey), getFilterFieldsOptionKey(resourceKey)],
                { force: true }
            )
        } finally {
            setIsDefaultsBusy(false)
        }
        onClose()
    }

    /** Адмін: видалити глобальний дефолт ресурсу */
    const handleResetDefault = async () => {
        if (!resourceKey) return
        setIsDefaultsBusy(true)
        try {
            await Promise.all([
                userManager.deleteGlobalOption(getDefaultTableColumnsOptionKey(resourceKey)),
                userManager.deleteGlobalOption(getDefaultFilterFieldsOptionKey(resourceKey)),
            ])
            await userManager.resolveUserOptions(
                [getTableColumnsOptionKey(resourceKey), getFilterFieldsOptionKey(resourceKey)],
                { force: true }
            )
        } finally {
            setIsDefaultsBusy(false)
        }
        initDraft()
    }

    return (
        <Drawer
            title={t('Display settings', { ns: 'Core' })}
            placement="right"
            width={420}
            open={open}
            onClose={onClose}
            extra={
                <Space>
                    <Button onClick={handleReset}>{t('Reset', { ns: 'Core' })}</Button>
                    <Button type="primary" onClick={handleSave}>
                        {t('Save', { ns: 'Core' })}
                    </Button>
                </Space>
            }
            footer={
                isDefaultsAdmin ? (
                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                        <Typography.Text type="secondary">
                            {t('Resource defaults for all users', { ns: 'Core' })}
                        </Typography.Text>
                        <Button block loading={isDefaultsBusy} onClick={handleSaveDefault}>
                            {t('Save as default for all', { ns: 'Core' })}
                        </Button>
                        <Button block danger loading={isDefaultsBusy} onClick={handleResetDefault}>
                            {t('Reset default for all', { ns: 'Core' })}
                        </Button>
                    </Space>
                ) : undefined
            }
        >
            <Tabs
                items={[
                    {
                        key: 'columns',
                        label: t('Columns', { ns: 'Core' }),
                        children: <ColumnsTab columns={draftColumns} onChange={setDraftColumns} />,
                    },
                    {
                        key: 'filter',
                        label: t('Filters', { ns: 'Core' }),
                        children: <FilterFieldsTab fields={draftFields} onChange={setDraftFields} />,
                    },
                ]}
            />
        </Drawer>
    )
}
